import React, { useState, useEffect, useRef } from 'react';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/events`);
      const data = await response.json();
      
      if (data.success) {
        setEvents(data.events);
      } else {
        setError('Error al cargar eventos');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    
    if (isToday) return 'Hoy';
    if (isTomorrow) return 'Mañana';
    
    return date.toLocaleDateString('es-ES', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const formatTime = (timeString) => {
    return timeString.slice(0, 5); // "18:00:00" -> "18:00"
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'rosary': return '📿';
      case 'mass': return '⛪';
      case 'prayer': return '🙏';
      case 'community': return '👥';
      default: return '🕊️';
    }
  };

  const getEventTypeName = (type) => {
    switch (type) {
      case 'rosary': return 'Rosario';
      case 'mass': return 'Misa';
      case 'prayer': return 'Oración';
      case 'community': return 'Comunidad';
      default: return 'Evento';
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'rosary': return '#9f7aea';
      case 'mass': return '#667eea';
      case 'prayer': return '#48bb78';
      case 'community': return '#ed8936';
      default: return '#4a5568';
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="events-section">
        <div className="events-loading">
          <div className="loading-spinner"></div>
          <p>Cargando eventos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="events-section">
        <div className="events-error">
          <p>⚠️ {error}</p>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return null; // No mostrar la sección si no hay eventos
  }

  return (
    <div className="events-section">
      <div className="events-header">
        <h3>📅 Actividades de Hoy</h3>
        <div className="events-counter">
          {events.length} evento{events.length !== 1 ? 's' : ''}
        </div>
      </div>

      <div className="events-container">
        {/* Botones de navegación solo en desktop */}
        <button 
          onClick={scrollLeft} 
          className="scroll-btn scroll-btn-left desktop-only"
          aria-label="Evento anterior"
        >
          ‹
        </button>

        <div className="events-scroll-container" ref={scrollContainerRef}>
          {events.map((event, index) => (
            <div key={event.id || index} className="event-card">
              <div className="event-header-row">
                <h4 className="event-title">{event.title}</h4>
                <div className="event-meta-right">
                  <div className="event-type-badge" style={{ backgroundColor: getEventTypeColor(event.type) }}>
                    <span className="event-icon">{getEventTypeIcon(event.type)}</span>
                    <span className="event-type-text">{getEventTypeName(event.type)}</span>
                  </div>
                  <div className="event-date-time">
                    <div className="event-date">{formatDate(event.date)}</div>
                    <div className="event-time">
                      {formatTime(event.time)} hs.
                      <span className="timezone-indicator"> 🇦🇷 (Argentina)</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="event-description">{event.description}</p>
              
              <div className="event-details">
                {event.location && (
                  <div className="event-detail">
                    <span className="detail-icon">📍</span>
                    <span className="detail-text">{event.location}</span>
                  </div>
                )}
                
                {event.isPresential && (
                  <div className="event-detail">
                    <span className="detail-icon">🏛️</span>
                    <span className="detail-text">Presencial</span>
                  </div>
                )}
                
                {event.isVirtual && event.meetingLink && (
                  <div className="event-detail">
                    <span className="detail-icon">💻</span>
                    <a 
                      href={event.meetingLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="meeting-link"
                    >
                      Unirse por Google Meet
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <button 
          onClick={scrollRight} 
          className="scroll-btn scroll-btn-right desktop-only"
          aria-label="Siguiente evento"
        >
          ›
        </button>
      </div>

      <style jsx>{`
        .events-section {
          margin-bottom: 30px;
        }

        .events-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .events-header h3 {
          color: #4a5568;
          margin: 0;
          font-size: 1.5em;
          font-weight: 600;
        }

        .events-counter {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          padding: 4px 12px;
          border-radius: 15px;
          font-size: 0.9em;
          font-weight: 600;
        }

        .events-container {
          position: relative;
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .events-scroll-container {
          display: flex;
          gap: 20px;
          overflow-x: auto;
          scroll-behavior: smooth;
          padding: 10px 0;
          flex: 1;
          scrollbar-width: thin;
          scrollbar-color: rgba(102, 126, 234, 0.3) transparent;
        }

        .events-scroll-container::-webkit-scrollbar {
          height: 6px;
        }

        .events-scroll-container::-webkit-scrollbar-track {
          background: rgba(102, 126, 234, 0.1);
          border-radius: 3px;
        }

        .events-scroll-container::-webkit-scrollbar-thumb {
          background: rgba(102, 126, 234, 0.3);
          border-radius: 3px;
        }

        .events-scroll-container::-webkit-scrollbar-thumb:hover {
          background: rgba(102, 126, 234, 0.5);
        }

        .event-card {
          background: white;
          border-radius: 15px;
          padding: 20px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          border-left: 4px solid #667eea;
          min-width: 360px; /* Aumentar ancho mínimo */
          max-width: 420px; /* Aumentar ancho máximo */
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .event-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }

        .event-header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 15px;
          gap: 15px;
        }

        .event-title {
          color: #4a5568;
          margin: 0;
          font-size: 1.2em;
          font-weight: 600;
          line-height: 1.3;
          flex: 1;
        }

        .event-meta-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 1; /* permitir que esta columna pueda encogerse */
          min-width: 0;   /* necesario en flexbox para que el contenido pueda ajustar su ancho */
        }

        .event-type-badge {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          font-size: 0.8em;
          white-space: nowrap;
        }

        .event-icon {
          font-size: 0.9em;
        }

        .event-type-text {
          text-transform: capitalize;
        }

        .event-date-time {
          text-align: right;
          max-width: 180px; /* limitar ancho para permitir envoltura del texto de la fecha */
          white-space: normal;
        }

        .event-date {
          font-weight: 600;
          color: #4a5568;
          font-size: 0.9em;
          margin-bottom: 2px;
          text-transform: capitalize;
          word-break: break-word;
          overflow-wrap: anywhere;
          white-space: normal;
        }

        .event-time {
          color: #718096;
          font-size: 0.85em;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .timezone-indicator {
          font-size: 0.75em;
          color: #a0aec0;
          font-weight: 400;
          white-space: nowrap;
        }

        .event-description {
          color: #718096;
          margin: 0 0 15px 0;
          line-height: 1.5;
          font-size: 0.95em;
        }

        .event-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .event-detail {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .detail-icon {
          font-size: 1em;
          width: 16px;
          text-align: center;
        }

        .detail-text {
          color: #4a5568;
          font-weight: 500;
          font-size: 0.9em;
        }

        .meeting-link {
          color: #667eea;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s ease;
          font-size: 0.9em;
        }

        .meeting-link:hover {
          color: #5a67d8;
          text-decoration: underline;
        }

        .scroll-btn {
          background: rgba(255, 255, 255, 0.95);
          border: 2px solid rgba(102, 126, 234, 0.2);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 1.5em;
          font-weight: bold;
          color: #667eea;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          z-index: 10;
        }

        .scroll-btn:hover {
          background: #667eea;
          color: white;
          transform: scale(1.1);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
        }

        .scroll-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .scroll-btn:disabled:hover {
          background: rgba(255, 255, 255, 0.95);
          color: #667eea;
          transform: none;
        }

        .desktop-only {
          display: flex;
        }

        .events-loading, .events-error {
          text-align: center;
          padding: 40px 20px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .events-loading p, .events-error p {
          color: #718096;
          margin: 0;
          font-weight: 500;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .desktop-only {
            display: none;
          }

          .events-container {
            gap: 0;
          }

          .events-scroll-container {
            gap: 15px;
          }

          .event-card {
            min-width: 280px;
            max-width: 350px;
            padding: 18px;
          }

          .event-header-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .event-meta-right {
            flex-direction: row;
            align-items: center;
            gap: 10px;
            align-self: flex-start;
          }

          .event-date-time {
            text-align: left;
          }

          .event-type-badge {
            font-size: 0.75em;
            padding: 4px 8px;
          }

          .event-title {
            font-size: 1.1em;
          }

          .event-description {
            font-size: 0.9em;
          }

          .event-details {
            gap: 6px;
          }

          .detail-text, .meeting-link {
            font-size: 0.85em;
          }
        }

        @media (max-width: 480px) {
          .event-card {
            min-width: 260px;
            max-width: 300px;
            padding: 15px;
          }

          .events-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default Events;